import { getSmoothStepPath, useInternalNode, EdgeLabelRenderer, useReactFlow } from "@xyflow/react";
import PropTypes from "prop-types";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useGetDialDataQuery } from "@/redux/api/dashboardApi";
import { useLiveDataWindow } from "../utils/useLiveDataWindow";
import { calculateFlowSpeed, determinePipeState } from "./flowAnimationUtils";

/**
 * PipeEdge – SCADA industrial pipe edge with real-time dynamic flow.
 *
 * This "Smart Pipe" automatically fetches data from its source component
 * to determine flow speed, direction, and operational state.
 */
const PipeEdge = ({
  id,
  source,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}) => {
  const sourceNode = useInternalNode(source);
  const nodeData = sourceNode?.data || {};
  const config = nodeData.config || {};
  const deviceId = nodeData.deviceId;
  const parameterId = nodeData.parameterId;

  const { dateRange } = useSelector((state) => state.dashboardV2 || { dateRange: {} });
  const liveWindow = useLiveDataWindow();

  // Build API params for live data fetch (matches BaseNode logic)
  const params = useMemo(() => {
    if (!deviceId || !parameterId) return null;
    const date = liveWindow.value;
    return {
      ...date,
      device: deviceId,
      peakHours: dateRange?.peakHours,
      deviceParameter: JSON.stringify({ _id_in: parameterId, populate: "systemFormula systemParameter device" }),
    };
  }, [deviceId, parameterId, dateRange, liveWindow]);

  // Fetch live data with polling
  const { data: apiData } = useGetDialDataQuery(params, {
    skip: !params,
    pollingInterval: 10000,
  });

  const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
  const rawValue = apiData?.data?.value;
  const rawDate = apiData?.data?.date;
  const dataTimestamp = rawDate ? new Date(rawDate).getTime() : null;
  const isWithin12Hours = dataTimestamp !== null && !Number.isNaN(dataTimestamp) && (Date.now() - dataTimestamp <= TWELVE_HOURS_MS);
  const hasValidValue = rawValue !== undefined && rawValue !== null && rawValue !== "" && rawValue !== "--" && !Number.isNaN(Number(rawValue));
  const hasLiveData = hasValidValue && isWithin12Hours;
  const liveValue = hasLiveData ? Number(rawValue) : null;
  const isOffline = Boolean(deviceId && (!hasLiveData || apiData?.deviceStatus === "inactive"));
  
  // Determine pipe state and speed from live data
  const pipeState = useMemo(() => {
    // Basic state from props/data
    const baseStyle = data?.style || {};
    
    if (isOffline) {
      return {
        ...baseStyle,
        active: false,
        warning: false,
        error: false,
        speed: 0,
        color: "#94a3b8",
      };
    }
    
    // If we have live data, use it to calculate speed and state
    if (liveValue !== undefined && liveValue !== null) {
      const calculatedSpeed = calculateFlowSpeed(liveValue, config.minValue || 0, config.maxValue || 100);
      const state = determinePipeState({ ...sourceNode, data: { ...nodeData, liveValue } });
      
      return {
        ...baseStyle,
        active: state.active,
        warning: state.warning,
        error: state.error,
        speed: (calculatedSpeed !== undefined && calculatedSpeed !== null) ? calculatedSpeed : (baseStyle.speed || 1),
        color: state.active ? (baseStyle.color || "#10b981") : "#94a3b8", // Green if active
      };
    }

    // Fallback to static data
    return {
      active: true,
      speed: 1,
      color: "#64748b",
      ...baseStyle,
    };
  }, [liveValue, nodeData, config, sourceNode, data, isOffline]);

  const { color: pipeColor, speed, active: isActive, warning: isWarning, error: isError, thickness = 4 } = pipeState;

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 12,
  });

  const { setEdges } = useReactFlow();

  // Configured direction: "forward" (default, source -> target) or "reverse" (target -> source)
  const configuredDirection = data?.style?.flowDirection || "forward";

  // Animation direction: normal follows source -> target, reverse flows backwards
  const animDirection = configuredDirection === "reverse" ? "reverse" : "normal";
  const animDuration = speed > 0 ? Math.max(0.2, 1.2 / speed) : 0;
  const animName = `pipe-flow-${id}`;

  // Flip direction handler for edit mode
  const handleFlipDirection = (e) => {
    e.stopPropagation();
    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === id) {
          const currentDir = edge.data?.style?.flowDirection || "forward";
          const nextDir = currentDir === "forward" ? "reverse" : "forward";
          return {
            ...edge,
            data: {
              ...edge.data,
              style: {
                ...(edge.data?.style || {}),
                flowDirection: nextDir,
              },
            },
          };
        }
        return edge;
      })
    );
  };

  // Colors for rendering
  const colors = useMemo(() => {
    if (isError) return { active: "#ef4444", outer: "#ef444420", flow: "#ffffff" };
    if (isWarning) return { active: "#f59e0b", outer: "#fbbf2420", flow: "#ffffff" };
    if (!isActive || speed <= 0) return { active: "#94a3b8", outer: "#94a3b810", flow: "transparent" };
    
    // Active flow colors (High contrast white flow over emerald/pipe color)
    const base = pipeColor || "#10b981";
    return { 
      active: base, 
      outer: `${base}20`, 
      flow: "#ffffff" // Always use white flow for best visibility on gray/green pipes
    };
  }, [isActive, isWarning, isError, speed, pipeColor]);

  return (
    <>
      <g className="live-pipe-edge">
        <defs>
          <style>{`
            @keyframes ${animName} {
              from { stroke-dashoffset: 0; }
              to { stroke-dashoffset: -20; }
            }
          `}</style>
        </defs>

        {/* Layer 1: Outer casing */}
        <path
          d={edgePath}
          fill="none"
          stroke={colors.outer}
          strokeWidth={thickness * 3}
          strokeLinecap="round"
        />

        {/* Layer 2: Inner pipe */}
        <path
          d={edgePath}
          fill="none"
          stroke={colors.active}
          strokeWidth={thickness}
          strokeLinecap="round"
        />

        {/* Layer 3: Animated flow (ALWAYS visible when active) */}
        {isActive && speed > 0 && (
          <path
            d={edgePath}
            fill="none"
            stroke={colors.flow}
            strokeWidth={Math.max(1.2, thickness - 2)}
            strokeLinecap="round"
            strokeDasharray="8 12"
            strokeOpacity="0.9"
            style={{
              animation: `${animName} ${animDuration}s linear infinite ${animDirection}`,
            }}
          />
        )}

        {/* Selection highlight */}
        {selected && (
          <path
            d={edgePath}
            fill="none"
            stroke="#3b82f6"
            strokeWidth={thickness + 4}
            strokeOpacity="0.3"
            strokeLinecap="round"
          />
        )}

        {/* Invisible hit area */}
        <path
          d={edgePath}
          fill="none"
          stroke="transparent"
          strokeWidth={24}
        />

        {/* Error Marker */}
        {isError && (
          <g transform={`translate(${(sourceX + targetX) / 2}, ${(sourceY + targetY) / 2})`}>
            <circle r="8" fill="#ef4444" />
            <text textAnchor="middle" y="4" fontSize="10" fontWeight="bold" fill="#fff">✕</text>
          </g>
        )}
      </g>

      {/* Flip Direction button: edit mode only when selected */}
      {selected && !data?.readOnly && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY - 18}px)`,
              pointerEvents: "all",
              zIndex: 1000,
            }}
            className="nodrag nopan"
          >
            <button
              type="button"
              className="live-pipe-edge__flip-btn"
              onClick={handleFlipDirection}
              title={`Flow is currently ${configuredDirection === "reverse" ? "Reverse" : "Forward"}. Click to flip.`}
            >
              Flip Direction
            </button>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

PipeEdge.propTypes = {
  id: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  sourceX: PropTypes.number.isRequired,
  sourceY: PropTypes.number.isRequired,
  targetX: PropTypes.number.isRequired,
  targetY: PropTypes.number.isRequired,
  sourcePosition: PropTypes.string,
  targetPosition: PropTypes.string,
  data: PropTypes.object,
  selected: PropTypes.bool,
};

export default PipeEdge;
